import { useRef, forwardRef, useImperativeHandle } from 'react';

import { useParams } from 'react-router-dom';

import { Row, Col } from 'antd';

import { Form, SectionWrapper, CustomNotification } from 'common/components';
import SupplementForm from './SupplementForm';
import SupplementNutrient from './SupplementNutrient';
import { useInvalidateGroupDetail } from '../../../shared/hooks';

import { useAddSupplementLabel } from '../../hook/useAddSupplementLabel';

import {
  SUPPLEMENT_NUTRIENT_NAME,
  SUPPLEMENT_INITIAL_FORM,
} from 'pages/product-full-view/components/product-media-area/nutrition-label/constants';

import Messages from 'i18n/messages/product';
import { useIntl } from 'react-intl';

import './AddSupplementLabel.less';
import TraceableCommentContainer from 'pages/traceable-comment/TraceableCommentContainer';

const AddSupplementLabel = forwardRef((props, forwardedRef) => {
  const intl = useIntl();
  const [form] = Form.useForm();

  const { id } = useParams();

  const nutrientsHandleRef = useRef();
  const ingredientsHandleRef = useRef();

  const { visible, factsPanel, typeView, onSubmitSuccess, onSubmitFail } =
    props;

  const mutation = useAddSupplementLabel({ productId: id, typeView });
  const { handleSyncAllProductDetailGroup, handleSyncAllQaDetailGroup } =
    useInvalidateGroupDetail(id);

  const initForm = (on) => {
    const initialData =
      on === 'add'
        ? SUPPLEMENT_INITIAL_FORM
        : {
          ...factsPanel,
          [SUPPLEMENT_NUTRIENT_NAME.NUTRIENT]: factsPanel.nutrients,
          [SUPPLEMENT_NUTRIENT_NAME.INGREDIENT]:
            factsPanel.dietaryIngredients,
        };

    form.setFieldsValue(initialData);
  };

  const submitForm = async () => {
    nutrientsHandleRef?.current?.getOriginalDataSubmitOnEdit();
    ingredientsHandleRef?.current?.getOriginalDataSubmitOnEdit();

    //const formValues = await form.validateFields();
    const formValues = form.getFieldsValue(true);

    //if (!formValues) return;

    const {
      [SUPPLEMENT_NUTRIENT_NAME.NUTRIENT]: nutrients,
      [SUPPLEMENT_NUTRIENT_NAME.INGREDIENT]: ingredients,
      _add, //*  payload excludes _add
      ...restFormValues
    } = formValues;

    const params = {
      productItemId: +id,
      data: {
        id: factsPanel?.id,
        nutrients: nutrients,
        dietaryIngredients: ingredients,
        ...restFormValues,
      },
    };

    mutation.mutate(params, {
      onSuccess: (result) => {
        if (result.isSuccess) {
          resetForm();
          handleSyncAllProductDetailGroup();
          handleSyncAllQaDetailGroup();
          onSubmitSuccess && onSubmitSuccess();

          CustomNotification.success('Update Supplement Fact successfully');
        } else {
          form.resetFields();

          onSubmitFail && onSubmitFail();

          CustomNotification.error('Something went wrong!');
        }
      },
    });
  };

  const resetForm = () => {
    form.resetFields();

    nutrientsHandleRef?.current?.resetState();
    ingredientsHandleRef?.current?.resetState();
  };

  const onFieldsChange = (changedFields, allFields) => {
    const caloriesField = allFields.find(
      (fieldItem) => fieldItem.name[0] === 'calories'
    );

    const caloriesValue = caloriesField?.value;

    if (!caloriesValue) form.setFieldsValue({ caloriesFromFat: null });
  };

  useImperativeHandle(forwardedRef, () => {
    return {
      submitForm,
      initForm,
      resetForm,
    };
  });

  const SupplementIngredient = SupplementNutrient;

  return visible ? (
    <Form
      form={form}
      className='supplement-form'
      layout='vertical'
      onFieldsChange={onFieldsChange}
    >
      <Row className='supplement-form__wrapper' wrap={false} gutter={16}>
        <Col span={8}>
          <SupplementForm productId={id} typeView={typeView} />
        </Col>
        <Col span={8}>
          <SectionWrapper
            className='supplement-form__nutrient'
            title={
              <>
                <TraceableCommentContainer
                  qaSpecName={'Fact Panels Supplement Information'}
                  qaSpecFieldName='Nutrients'
                >
                  {intl.formatMessage(Messages.supplementLabelNutrients)}
                </TraceableCommentContainer>
              </>
            }
          >
            <SupplementNutrient
              ref={nutrientsHandleRef}
              name={SUPPLEMENT_NUTRIENT_NAME.NUTRIENT}
              productId={id}
              typeView={typeView}
            />
          </SectionWrapper>
        </Col>
        <Col span={8}>
          <SectionWrapper
            className='supplement-form__nutrient'
            title={
              <>
                <TraceableCommentContainer
                  qaSpecName={'Fact Panels Supplement Information'}
                  qaSpecFieldName='Dietary Ingredients'
                >
                  {intl.formatMessage(
                    Messages.supplementLabelDietaryIngredient
                  )}
                </TraceableCommentContainer>
              </>
            }
          >
            <SupplementIngredient
              ref={ingredientsHandleRef}
              name={SUPPLEMENT_NUTRIENT_NAME.INGREDIENT}
              productId={id}
              typeView={typeView}
            />
          </SectionWrapper>
        </Col>
      </Row>
    </Form>
  ) : null;
});

export default AddSupplementLabel;
